<script setup lang="ts">
import type { useFeaturedVideosModal } from '~/composables/useFeaturedVideosModal';
import type { FeaturedVideoProperty } from '~/core/entities/property';
import type { useFeaturedVideosStore } from '~/store/featuredVideosStore';
import type { ResultTuple } from '~~/core/helpers/result';

import { Button } from '~/components/ui/button';

import IconMaterialSymbolsLightPlayArrowRounded from '~icons/material-symbols-light/play-arrow-rounded';

const emit = defineEmits<{
	(e: 'open-feed'): void;
}>();

function normalizeThumbUrl(url: string | null | undefined): string | null {
	if (!url) return null;
	return url.replace(/\.(webp|png|jpeg|gif)(?=\?|#|$)/i, '.jpg');
}

// SSR-compatible fetch: makes the thumbnail available during SSR so
// <NuxtImg preload> can inject <link rel="preload"> in the initial HTML.
const { data: ssrThumbnailUrl } = useAsyncData(
	'header-video-thumbnail',
	() =>
		$fetch<ResultTuple<{ properties: FeaturedVideoProperty[] }>>(
			'/api/properties/featured-videos',
			{ query: { page: 1, perPage: 1 } }
		),
	{
		transform: response => {
			const [result] = response ?? [];
			const thumbnail = result?.properties?.[0]?.featuredVideos?.[0]?.thumbnail;
			return normalizeThumbUrl(thumbnail);
		}
	}
);

// Lazy initialization for full store/modal (client-only interactions)
const store = shallowRef<ReturnType<typeof useFeaturedVideosStore> | null>(
	null
);
const modal = shallowRef<ReturnType<typeof useFeaturedVideosModal> | null>(
	null
);
const isClientReady = ref(false);

onMounted(async () => {
	const [{ useFeaturedVideosStore }, { useFeaturedVideosModal }] =
		await Promise.all([
			import('~/store/featuredVideosStore'),
			import('~/composables/useFeaturedVideosModal')
		]);
	store.value = useFeaturedVideosStore();
	modal.value = useFeaturedVideosModal();
	isClientReady.value = true;
});

const firstVideo = computed(() => {
	if (!isClientReady.value || !store.value || !modal.value) return null;

	if (store.value.videos[0]) {
		return store.value.videos[0];
	}
	const historyVideo = modal.value.historyItems.value?.[0];
	if (historyVideo) {
		return {
			property: historyVideo.property,
			videoUrl: historyVideo.videoUrl,
			thumbnailUrl: historyVideo.thumbnailUrl
		};
	}
	return null;
});

// SSR: uses the pre-fetched thumbnail; client: switches to store data once ready
const thumbnailUrl = computed(() => {
	if (isClientReady.value && firstVideo.value) {
		return normalizeThumbUrl(firstVideo.value.thumbnailUrl);
	}
	return ssrThumbnailUrl.value;
});

const hasVideo = computed(() => {
	if (!isClientReady.value || !store.value || !modal.value) return true;

	return (
		!store.value.isPreloaded ||
		!modal.value.isHistoryLoaded.value ||
		!!firstVideo.value ||
		store.value.allWatched
	);
});

function handleClick() {
	emit('open-feed');
}
</script>

<template>
	<Button
		v-if="hasVideo"
		variant="transparent"
		size="xs"
		:is-client-only="false"
		class="group relative flex items-center gap-2 overflow-hidden rounded-full border-none bg-transparent transition-[transform,background-color,box-shadow] active:scale-95 sm:bg-white/10 sm:pr-3 sm:hover:bg-white/20 sm:hover:shadow-lg sm:hover:shadow-white/10"
		aria-label="Ver vídeos em destaque"
		@click="handleClick"
	>
		<div class="relative">
			<div
				class="absolute -inset-1 animate-pulse rounded-full bg-white/30 duration-[2s]"
			/>

			<div
				class="relative size-11 overflow-hidden rounded-full border-2 border-white/60 transition-all group-hover:border-white sm:size-10"
			>
				<NuxtImg
					v-if="thumbnailUrl"
					:src="thumbnailUrl"
					alt="Preview do vídeo"
					width="44"
					height="44"
					format="webp"
					quality="80"
					class="size-full object-cover"
					:preload="{ fetchPriority: 'high' }"
					fetchpriority="high"
					decoding="async"
				/>
				<div v-else class="size-full bg-neutral-800" />

				<div
					class="absolute inset-0 flex items-center justify-center bg-black/20"
				>
					<IconMaterialSymbolsLightPlayArrowRounded
						class="size-5 text-white drop-shadow"
					/>
				</div>
			</div>
		</div>

		<span class="hidden text-sm font-medium text-white sm:inline">
			Descubra
		</span>
	</Button>
</template>
